import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Inbox, ArrowLeft, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PortalInbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/threads");
      setThreads(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openThread = async (clientId) => {
    setOpenId(clientId);
    setDetail(null);
    try {
      const r = await api.get(`/portal/threads/${clientId}`);
      setDetail(r.data);
      // mark unread messages as read
      await api.post(`/portal/threads/${clientId}/mark-read`).catch(() => {});
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !openId) return;
    setSending(true);
    try {
      await api.post(`/portal/threads/${openId}/reply`, { body: reply.trim() });
      setReply("");
      await openThread(openId);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (openId) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center gap-3">
          <button onClick={() => { setOpenId(null); setDetail(null); }} className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid="btn-thread-back">
            <ArrowLeft className="w-4 h-4" /> Back to inbox
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Family thread</div>
            <h1 className="font-heading text-xl font-semibold" data-testid="thread-client-name">{detail?.client_name || "…"}</h1>
          </div>
        </div>
        <div className="p-6 max-w-3xl">
          {!detail ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              <ul className="space-y-3 mb-6" data-testid="thread-messages">
                {detail.messages.map((m) => {
                  const isOut = m.direction === "out";
                  return (
                    <li key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-md p-3 border ${isOut ? "bg-sky-50 border-sky-200" : "bg-white border-slate-200"}`}>
                        <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1.5">
                          <User className="w-3 h-3" /> {m.sender_name}
                          {m.relationship ? ` · ${m.relationship}` : ""}
                          {isOut ? " (care team)" : " (family)"}
                        </div>
                        <div className="text-sm text-slate-900 whitespace-pre-wrap">{m.body}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="bg-white border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Reply as {user?.name || user?.email}</div>
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  maxLength={2000}
                  placeholder="Reply to the family…"
                  className="w-full p-3 border border-slate-300 bg-white text-sm"
                  data-testid="thread-reply-body"
                />
                <div className="flex justify-between items-center mt-2">
                  <div className="text-[10px] text-slate-400">{reply.length}/2000</div>
                  <Button size="sm" onClick={sendReply} disabled={sending || !reply.trim()} data-testid="btn-thread-send" className="bg-slate-900 hover:bg-slate-800">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send reply"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Inbox className="w-3 h-3" /> People</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="inbox-title">Family Inbox</h1>
        </div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="inbox-loading">Loading…</div>
        ) : threads.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="inbox-empty">
            No family messages yet. When a family member writes from a portal link, their message will appear here.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="inbox-list">
            {threads.map((t) => (
              <li key={t.client_id}>
                <button
                  onClick={() => openThread(t.client_id)}
                  data-testid={`thread-${t.client_id}`}
                  className="w-full bg-white border border-slate-200 hover:border-sky-400 hover:bg-sky-50 p-4 text-left transition-colors flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900 truncate">{t.client_name || t.client_id}</div>
                      {t.unread_count > 0 && (
                        <span data-testid={`thread-unread-${t.client_id}`} className="text-[9px] uppercase tracking-widest bg-red-500 text-white px-1.5 py-0.5">{t.unread_count} new</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 truncate">
                      <span className="font-semibold">{t.latest_direction === "out" ? "You: " : ""}</span>{t.latest_body}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {t.in_count} from family · {t.out_count} from care team · {t.latest_at ? new Date(t.latest_at).toLocaleString() : ""}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
